import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Sparkles, Loader2, Rocket } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("owner@aurelia.de");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user && typeof user === "object") return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Erfolgreich angemeldet");
      nav("/", { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0b0f17] text-slate-100" data-testid="login-page">
      {/* Left – brand panel */}
      <div className="hidden lg:flex relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(16,185,129,0.18),transparent_50%),radial-gradient(ellipse_at_bottom_right,rgba(99,102,241,0.18),transparent_50%)]" />
        <div className="relative z-10 p-12 flex flex-col justify-between w-full">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-emerald-500/20 text-emerald-400 grid place-items-center">
              <Rocket className="h-5 w-5" />
            </div>
            <div className="font-bold tracking-tight">Marketing Rocket</div>
          </div>
          <div>
            <h1 className="text-4xl font-extrabold tracking-tight leading-tight">
              Dein Marketing-Autopilot.
            </h1>
            <p className="mt-4 text-slate-400 max-w-md">
              Produkte, Leads, Kampagnen und Videos an einem Ort — und ein KI-Agent,
              der mit deiner Freigabe im Hintergrund weiterarbeitet.
            </p>
            <div className="mt-8 flex gap-6 text-xs text-slate-500">
              <div><div className="text-emerald-400 text-lg font-bold">8</div>Produkte</div>
              <div><div className="text-emerald-400 text-lg font-bold">10</div>Leads</div>
              <div><div className="text-emerald-400 text-lg font-bold">6</div>Deals</div>
            </div>
          </div>
          <div className="text-xs text-slate-500">© Marketing Rocket</div>
        </div>
      </div>

      {/* Right – login form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <Card className="w-full max-w-md border-white/5 bg-white/[0.03] backdrop-blur">
          <CardContent className="p-8">
            <div className="mb-6">
              <div className="text-xs font-semibold uppercase tracking-wider text-emerald-400">Anmeldung</div>
              <h2 className="text-2xl font-bold mt-1 tracking-tight">Willkommen zurück</h2>
              <p className="text-sm text-slate-400 mt-1">Melde dich mit deinem Owner-Konto an.</p>
            </div>

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-slate-300">E-Mail</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-slate-500"
                  placeholder="owner@aurelia.de"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-slate-300">Passwort</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                  className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-slate-500"
                  placeholder="••••••••"
                />
              </div>

              {error ? (
                <div className="text-sm text-red-400" data-testid="login-error">{error}</div>
              ) : null}

              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit-button"
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Anmelden
              </Button>
            </form>

            <div className="mt-6 text-xs text-slate-500 border-t border-white/5 pt-4">
              Owner-Zugang voreingestellt. Passwort siehe <code className="text-slate-300">/app/memory/test_credentials.md</code>.
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
